<script setup>
import { ref, computed, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const { agentsList } = useAgentsList();

const currentChat = computed(() => store.getters.getSelectedChat);
const teams = useMapGetter('teams/getTeams');
const currentUser = useMapGetter('getCurrentUser');

const selectedTeamId = ref('');
const selectedAgentId = ref('');
const comment = ref('');
const saving = ref(false);

// Agente habilita só após escolher Time (estilo Digisac)
const agentDisabled = computed(() => !selectedTeamId.value);

// 🔴 A LISTA DE ATENDENTES É A DO TIME ESCOLHIDO (16/09/2026).
// Trazia todos da conta: dava para transferir para o Previdenciário e
// escolher alguém do Trabalhista. Se a busca dos membros falhar, a lista
// volta a ser a da conta inteira — com um aviso, para ninguém achar que é
// o time.
const membrosFalharam = ref(false);
const membrosDoTime = computed(() =>
  selectedTeamId.value
    ? store.getters['teamMembers/getTeamMembers'](selectedTeamId.value)
    : []
);
// 🔴 TIME SEM MEMBROS = TODA A EQUIPE (17/09/2026, Dr. Henrique: "tá
// bugado para transferir"). No A&T nenhum dos sete times tem membros
// cadastrados; filtrando por eles, a lista de atendentes vinha VAZIA e não
// dava para escolher ninguém.
const timeSemMembros = computed(
  () => Boolean(selectedTeamId.value) && !membrosDoTime.value.length
);
const agentesDoTime = computed(() => {
  if (membrosFalharam.value || timeSemMembros.value) return agentsList.value;
  const ids = new Set(membrosDoTime.value.map(m => m.id));
  return agentsList.value.filter(agente => ids.has(agente.id));
});

// Trocar de time desfaz a escolha de agente: a pessoa estava escolhendo
// alguém PARA aquele time.
watch(selectedTeamId, async teamId => {
  selectedAgentId.value = '';
  membrosFalharam.value = false;
  if (!teamId) return;
  try {
    await store.dispatch('teamMembers/get', { teamId });
  } catch {
    if (teamId === selectedTeamId.value) membrosFalharam.value = true;
  }
});

// A bolinha de disponibilidade: teal é o verde do advup (online), slate é
// ausente — no seletor e na legenda.
const COR_ONLINE = 'rgb(var(--teal-9))';
const COR_AUSENTE = 'rgb(var(--slate-8))';
const corDoAgente = agent =>
  agent.availability_status === 'online' ? COR_ONLINE : COR_AUSENTE;

const opcoesDeTime = computed(() =>
  teams.value.map(time => ({ label: time.name, value: time.id }))
);
const opcoesDeAgente = computed(() =>
  agentesDoTime.value.map(agente => ({
    label: agente.name,
    value: agente.id,
    color: corDoAgente(agente),
  }))
);
const online = computed(() =>
  agentesDoTime.value.filter(a => a.availability_status === 'online')
);

async function confirmTransfer() {
  if (!selectedTeamId.value) return;
  saving.value = true;
  try {
    await store.dispatch('assignTeam', {
      conversationId: currentChat.value.id,
      teamId: Number(selectedTeamId.value),
    });
    if (selectedAgentId.value) {
      await store.dispatch('assignAgent', {
        conversationId: currentChat.value.id,
        agentId: Number(selectedAgentId.value),
      });
    }
    if (comment.value.trim()) {
      await store.dispatch('createPendingMessageAndSend', {
        conversationId: currentChat.value.id,
        message: comment.value.trim(),
        private: true,
        sender: {
          name: currentUser.value.name,
          thumbnail: currentUser.value.avatar_url,
        },
      });
    }
    useAlert(t('CONVERSATION.TRANSFER_MODAL.SUCCESS'));
    emit('close');
  } catch {
    useAlert(t('CONVERSATION.TRANSFER_MODAL.ERROR'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="advup-modal-fundo" @click.self="emit('close')">
    <!-- O Teleport saiu para quem abre (ConversationHeader): com ele na raiz, o
         <Transition> de saída não alcançava a folha (24/10/2026). O comentário fica
         DENTRO da raiz (antes dela, vira fragmento em desenvolvimento). -->
    <div
      v-modal-acessivel="() => emit('close')"
      class="advup-modal advup-modal--claro advup-modal--estreito advup-modal--teto"
    >
      <!-- Teal: transferir é assunto de PESSOA, a mesma cor do botão que
             abre este modal no alto da conversa. -->
      <div class="advup-modal__topo advup-modal__topo--teal">
        <!-- Topo claro da Agenda (§5.10), como nos outros módulos — era o
               degradê escuro (05/10/2026). -->
        <i
          class="advup-modal__marca i-lucide-arrow-left-right"
          aria-hidden="true"
        />
        <span class="advup-modal__selo" aria-hidden="true">
          <i class="i-lucide-arrow-left-right" />
        </span>
        <div class="min-w-0 flex-1">
          <h2 class="advup-modal__titulo">
            {{ t('CONVERSATION.TRANSFER_MODAL.TITLE') }}
          </h2>
        </div>
        <button
          type="button"
          class="advup-modal__fechar"
          :aria-label="t('CONVERSATION.TRANSFER_MODAL.CANCEL')"
          @click="emit('close')"
        >
          <i class="i-lucide-x" />
        </button>
      </div>

      <div class="advup-modal__corpo tf-corpo">
        <!-- O formulário começa pelo time: o foco abria no comentário. -->
        <div data-campo-inicial>
          <span class="advup-rotulo">
            {{ t('CONVERSATION.TRANSFER_MODAL.TEAM_LABEL') }}
          </span>
          <AdvupSelect
            v-model="selectedTeamId"
            :options="opcoesDeTime"
            :placeholder="t('CONVERSATION.TRANSFER_MODAL.SELECT')"
            :rotulo="t('CONVERSATION.TRANSFER_MODAL.TEAM_LABEL')"
            :disabled="!teams.length"
            min-width="100%"
          />
          <p v-if="!teams.length" class="advup-nota tf-nota">
            {{ t('CONVERSATION.TRANSFER_MODAL.NO_TEAMS') }}
          </p>
        </div>

        <div>
          <span class="advup-rotulo">
            {{ t('CONVERSATION.TRANSFER_MODAL.AGENT_LABEL') }}
          </span>
          <AdvupSelect
            v-model="selectedAgentId"
            :options="opcoesDeAgente"
            :placeholder="
              agentDisabled
                ? t('CONVERSATION.TRANSFER_MODAL.PICK_TEAM_FIRST')
                : t('CONVERSATION.TRANSFER_MODAL.SELECT')
            "
            :rotulo="t('CONVERSATION.TRANSFER_MODAL.AGENT_LABEL')"
            :disabled="agentDisabled"
            min-width="100%"
          />
          <p v-if="membrosFalharam" class="advup-nota tf-nota">
            {{ t('CONVERSATION.TRANSFER_MODAL.MEMBERS_FAILED') }}
          </p>
          <p v-else-if="timeSemMembros" class="advup-nota tf-nota">
            {{ t('CONVERSATION.TRANSFER_MODAL.NO_MEMBERS') }}
          </p>
          <!-- Quem está online agora, à vista: é o que decide para quem
                 passar sem abrir a lista. -->
          <div
            v-if="!agentDisabled && online.length"
            class="tf-online"
            role="group"
            :aria-label="t('CONVERSATION.TRANSFER_MODAL.ONLINE_NOW')"
          >
            <span
              v-for="agent in online.slice(0, 8)"
              :key="agent.id"
              class="advup-etq tf-online__pessoa"
            >
              <span class="tf-online__ponto" aria-hidden="true" />
              <span>{{ agent.name }}</span>
            </span>
          </div>
        </div>

        <div>
          <label for="tf-comentario" class="advup-rotulo">
            {{ t('CONVERSATION.TRANSFER_MODAL.COMMENT_LABEL') }}
          </label>
          <textarea
            id="tf-comentario"
            v-model="comment"
            rows="3"
            class="advup-campo advup-campo--area reset-base"
            :placeholder="t('CONVERSATION.TRANSFER_MODAL.COMMENT_PLACEHOLDER')"
          />
          <p class="advup-nota tf-nota">
            {{ t('CONVERSATION.TRANSFER_MODAL.COMMENT_HINT') }}
          </p>
        </div>
      </div>

      <div class="advup-modal__rodape advup-modal__rodape--acoes">
        <button type="button" class="advup-btn" @click="emit('close')">
          {{ t('CONVERSATION.TRANSFER_MODAL.CANCEL') }}
        </button>
        <button
          type="button"
          class="advup-btn-forte"
          :disabled="!selectedTeamId || saving"
          @click="confirmTransfer"
        >
          <i
            :class="
              saving
                ? 'i-lucide-loader-2 advup-gira'
                : 'i-lucide-arrow-left-right'
            "
            aria-hidden="true"
          />
          {{
            saving
              ? t('CONVERSATION.TRANSFER_MODAL.SAVING')
              : t('CONVERSATION.TRANSFER_MODAL.CONFIRM')
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* A casca, o topo, o corpo e o rodapé são do modal do produto; os campos são
   `AdvupSelect` e `.advup-campo`. Aqui mora só o respiro e a legenda de quem
   está online. */
.tf-corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-4);
}

.tf-online {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-1);
  margin-top: var(--esp-2);
}

.tf-online__pessoa {
  gap: var(--esp-1);
}

.tf-online__ponto {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: var(--r-redondo);
  background: rgb(var(--teal-9));
}

.tf-nota {
  margin: var(--esp-1) 0 0;
}
</style>
